/**
 * Vetting Page - SME Observation Review Workflow
 * 
 * WHY: This is the core "Weapon System" of SoftwareIQ per the UI Expert.
 * Analysts process hundreds of observations daily. The page is optimized
 * for speed with keyboard shortcuts (j/k/v/f/m/d) and high information density.
 * 
 * @see .docs/architecture/software_iq_architecture_review.md - Expert 2
 */

import { useState, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { api, VettingDecision, ObservationStatus, type Observation } from '@/api'
import { useVettingShortcuts, VETTING_SHORTCUTS_HELP } from '@/hooks/useKeyboardShortcuts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/useToast'
import {
  ClipboardCheck,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  FileCode,
  Keyboard,
} from 'lucide-react'

export function VettingPage() {
  const { user } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [selectedStatus, setSelectedStatus] = useState<ObservationStatus | 'pending'>('pending')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showShortcuts, setShowShortcuts] = useState(false)

  // Fetch observations using new modular API
  const { data: observations = [], isLoading } = useQuery({
    queryKey: ['softwareiq', 'observations', selectedStatus],
    queryFn: () => api.softwareiq.observations.list({ status: selectedStatus as ObservationStatus }),
    staleTime: 0, // Real-time queue, always refetch
  })

  // JFAC methodology for enhanced vetting workflow
  useQuery({
    queryKey: ['metadata', 'methodologies', 'jfac-2019'],
    queryFn: () => api.metadata.methodologies.get('jfac-2019'),
  })

  // Get currently selected observation for keyboard actions
  const currentObservation = useMemo(
    () => observations[selectedIndex] as Observation | undefined,
    [observations, selectedIndex]
  )

  // Vetting mutation using new modular API
  const vetMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: VettingDecision }) =>
      api.softwareiq.observations.vet(id, {
        status,
        confidence_level: 'Medium',
        exploit_maturity: 'Unproven',
        notes: `Vetted by ${user?.username || 'analyst'}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['softwareiq', 'observations'] })
      toast({
        title: 'Observation Vetted',
        description: 'The observation has been processed successfully.',
      })
      setExpandedId(null)
      // Move to next observation after vetting
      if (selectedIndex < observations.length - 1) {
        setSelectedIndex(selectedIndex + 1)
      }
    },
    onError: (error) => {
      toast({
        title: 'Vetting Failed',
        description: error instanceof Error ? error.message : 'Failed to vet observation',
        variant: 'destructive',
      })
    },
  })

  // Handle vetting action
  const handleVet = useCallback(
    (id: string, status: VettingDecision) => {
      if (vetMutation.isPending) return
      vetMutation.mutate({ id, status })
    },
    [vetMutation]
  )

  // Keyboard navigation handlers
  const handleNext = useCallback(() => {
    setSelectedIndex((prev) => Math.min(prev + 1, observations.length - 1))
  }, [observations.length])

  const handlePrev = useCallback(() => {
    setSelectedIndex((prev) => Math.max(prev - 1, 0))
  }, [])

  const handleExpand = useCallback(() => {
    if (currentObservation) {
      setExpandedId((prev) =>
        prev === currentObservation.observation_id ? null : currentObservation.observation_id
      )
    }
  }, [currentObservation])

  const handleVetCurrent = useCallback(
    (status: VettingDecision) => {
      if (currentObservation && selectedStatus === 'pending') {
        handleVet(currentObservation.observation_id, status)
      }
    },
    [currentObservation, selectedStatus, handleVet]
  )

  // Register keyboard shortcuts (only when in pending status)
  useVettingShortcuts(
    {
      onNext: handleNext,
      onPrev: handlePrev,
      onExpand: handleExpand,
      onConfirm: () => handleVetCurrent('confirmed'),
      onFalsePositive: () => handleVetCurrent('false_positive'),
      onMitigated: () => handleVetCurrent('mitigated'),
      onDeferred: () => handleVetCurrent('deferred'),
    },
    { enabled: selectedStatus === 'pending' && !isLoading }
  )

  const getSeverityVariant = (severity: string) => {
    const lower = severity.toLowerCase()
    if (lower === 'critical') return 'critical'
    if (lower === 'high') return 'high'
    if (lower === 'medium') return 'medium'
    return 'low'
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold">Vetting Queue</h2>
          <p className="text-muted-foreground">
            Review and vet scanner observations before promotion to findings
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Keyboard shortcuts toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowShortcuts(!showShortcuts)}
            className="gap-2"
          >
            <Keyboard className="h-4 w-4" />
            <span className="hidden sm:inline">Shortcuts</span>
          </Button>
          <Badge variant="info" className="text-lg px-4 py-2">
            {observations.length} {selectedStatus === 'pending' ? 'Pending' : 'Items'}
          </Badge>
        </div>
      </div>

      {/* Keyboard shortcuts help panel */}
      {showShortcuts && (
        <Card className="bg-muted/30">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-6">
              {VETTING_SHORTCUTS_HELP.map(({ key, description }) => (
                <div key={key} className="flex items-center gap-2">
                  <kbd className="px-2 py-1 text-xs font-mono bg-background border rounded shadow-sm">
                    {key}
                  </kbd>
                  <span className="text-sm text-muted-foreground">{description}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status filter */}
      <div className="flex gap-2">
        {(['pending', 'confirmed', 'false_positive', 'mitigated'] as const).map((status) => (
          <Button
            key={status}
            variant={selectedStatus === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedStatus(status)}
          >
            {status === 'pending' && <Clock className="h-4 w-4 mr-1" />}
            {status === 'confirmed' && <AlertTriangle className="h-4 w-4 mr-1" />}
            {status === 'false_positive' && <XCircle className="h-4 w-4 mr-1" />}
            {status === 'mitigated' && <CheckCircle className="h-4 w-4 mr-1" />}
            {status.replace('_', ' ')}
          </Button>
        ))}
      </div>

      {/* Observations list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-4 stagger-fade-in">
          {observations.map((obs, index) => {
            const isSelected = index === selectedIndex
            const isExpanded = expandedId === obs.observation_id
            
            return (
            <Card
              key={obs.observation_id}
              className={`transition-all ${isExpanded ? 'ring-2 ring-primary' : ''} ${
                isSelected && !isExpanded ? 'border-primary/50 bg-primary/5' : ''
              }`}
            >
              <CardHeader
                className="cursor-pointer"
                onClick={() => {
                  setSelectedIndex(index)
                  setExpandedId(isExpanded ? null : obs.observation_id)
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Badge variant={getSeverityVariant(obs.severity)}>{obs.severity}</Badge>
                    <CardTitle className="text-base">{obs.title}</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-muted-foreground">
                      {obs.scanner_name}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 text-muted-foreground transition-transform ${
                        expandedId === obs.observation_id ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </div>
              </CardHeader>

              {expandedId === obs.observation_id && (
                <CardContent className="space-y-4 border-t border-border pt-4">
                  {/* Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">CWE</span>
                      <p className="font-mono text-primary">{obs.cwe_id || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">CVE</span>
                      <p className="font-mono">{obs.cve_id || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Confidence</span>
                      <p>{obs.initial_confidence}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Maturity</span>
                      <p>{obs.initial_maturity}</p>
                    </div>
                  </div>

                  {/* File location */}
                  {obs.file_path && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                      <FileCode className="h-4 w-4 text-muted-foreground" />
                      <span className="font-mono text-sm">
                        {obs.file_path}
                        {obs.line_number && `:${obs.line_number}`}
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  {obs.description && (
                    <div>
                      <span className="text-sm text-muted-foreground">Description</span>
                      <p className="mt-1 text-sm">{obs.description}</p>
                    </div>
                  )}

                  {/* Vetting actions */}
                  {selectedStatus === 'pending' && (
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                      <Button
                        variant="destructive"
                        onClick={() => handleVet(obs.observation_id, 'confirmed')}
                        disabled={vetMutation.isPending}
                        title="Press 'v' to confirm"
                      >
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Confirm Finding
                        <kbd className="ml-2 px-1.5 py-0.5 text-xs bg-destructive-foreground/20 rounded hidden sm:inline">v</kbd>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleVet(obs.observation_id, 'false_positive')}
                        disabled={vetMutation.isPending}
                        title="Press 'f' for false positive"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        False Positive
                        <kbd className="ml-2 px-1.5 py-0.5 text-xs bg-muted rounded hidden sm:inline">f</kbd>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleVet(obs.observation_id, 'mitigated')}
                        disabled={vetMutation.isPending}
                        title="Press 'm' for mitigated"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Mitigated
                        <kbd className="ml-2 px-1.5 py-0.5 text-xs bg-muted rounded hidden sm:inline">m</kbd>
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => handleVet(obs.observation_id, 'deferred')}
                        disabled={vetMutation.isPending}
                        title="Press 'd' to defer"
                      >
                        <Clock className="h-4 w-4 mr-2" />
                        Defer
                        <kbd className="ml-2 px-1.5 py-0.5 text-xs bg-muted rounded hidden sm:inline">d</kbd>
                      </Button>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )})}  {/* Close observations.map */}

          {observations.length === 0 && (
            <div className="text-center py-12">
              <ClipboardCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium">No observations</h3>
              <p className="text-muted-foreground mt-1">
                {selectedStatus === 'pending'
                  ? 'All observations have been vetted. Great job!'
                  : `No observations with status "${selectedStatus.replace('_', ' ')}"`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
